import React, { useState, useEffect, useCallback, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  useMediaQuery,
  useTheme,
  Tooltip,
  Fade,
  Badge,
  Popover,
  Chip,
  Button,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import {
  Dashboard,
  Event,
  People,
  PersonAdd,
  Assessment,
  Inventory,
  AttachMoney,
  Payments,
  Settings,
  Logout,
  Receipt,
  Close as CloseIcon,
  MoreHoriz,
  Security,
  Groups,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material";
import R1 from "../../images/R1.webp";
import { useAuth } from "../../contexts/AuthContext";
import { designTokens } from "../../styles/theme";
import WelcomePopup from "../common/WelcomePopup";
import api from "../../services/api";

const drawerWidth = 260;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);

  // ── Notificaciones: solicitudes de anticipo pendientes ────────────────────
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [loadingAlertas, setLoadingAlertas] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const pollingRef = useRef(null);

  const fetchAlertas = useCallback(async (silent = false) => {
    if (!silent) setLoadingAlertas(true);
    try {
      const res = await api.get("/comisiones?sub=alertas");
      const lista = res.data?.alertas || res.data?.data?.alertas || [];
      setAlertas(Array.isArray(lista) ? lista : []);
    } catch {
      // silencioso — no interrumpir navegación si falla
    } finally {
      if (!silent) setLoadingAlertas(false);
    }
  }, []);

  // Polling cada 90 segundos
  useEffect(() => {
    fetchAlertas();
    pollingRef.current = setInterval(() => fetchAlertas(true), 30000);
    return () => clearInterval(pollingRef.current);
  }, [fetchAlertas]);

  // Re-fetch al cambiar de página (el usuario pudo pagar desde Comisiones)
  useEffect(() => {
    fetchAlertas(true);
  }, [location.pathname, fetchAlertas]);

  // Re-fetch cuando el tab vuelve a ser visible
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchAlertas(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchAlertas]);

  const handleAprobarAnticipo = async (alerta) => {
    setProcessingId(alerta.id);
    try {
      await api.put(`/comisiones?id=${alerta.id}`, { accion: "pagar_anticipo" });
      await fetchAlertas(true);
    } catch (err) {
      console.error("[Notif] Error aprobando anticipo:", err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleRechazarAnticipo = async (alerta) => {
    setProcessingId(`reject-${alerta.id}`);
    try {
      await api.put(`/comisiones?id=${alerta.id}`, { accion: "rechazar_anticipo" });
      await fetchAlertas(true);
    } catch (err) {
      console.error("[Notif] Error rechazando anticipo:", err);
    } finally {
      setProcessingId(null);
    }
  };

  const mainMenuItems = [
    { text: "Dashboard", icon: <Dashboard />, path: "/admin/dashboard" },
    { text: "Eventos", icon: <Event />, path: "/admin/eventos" },
    { text: "Clientes", icon: <People />, path: "/admin/clientes" },
    {
      text: "Colaboradores",
      icon: <PersonAdd />,
      path: "/admin/colaboradores",
    },
    { text: "Servicios", icon: <Assessment />, path: "/admin/servicios" },
    { text: "Gastos", icon: <Receipt />, path: "/admin/gastos" },
    { text: "Insumos", icon: <Inventory />, path: "/admin/insumos" },
    { text: "Finanzas", icon: <AttachMoney />, path: "/admin/finanzas" },
    { text: "Comisiones", icon: <Payments />, path: "/admin/comisiones" },
    { text: "Reclutamiento", icon: <Groups />, path: "/admin/perfil?tab=rh" },
  ];

  // Items para navegación inferior en mobile (los 4 más importantes)
  const bottomNavItems = mainMenuItems.slice(0, 4);
  const moreNavItems = mainMenuItems.slice(4);

  const userMenuItems = [
    { text: "Seguridad", icon: <Security />, path: "/admin/perfil?tab=seguridad" },
    { text: "Configuración", icon: <Settings />, path: "/admin/configuracion" },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMoreMenuOpen = (event) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchor(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate("/login");
  };

  const handleNavigate = (path) => {
    navigate(path);
    handleMoreMenuClose();
    if (isMobile) setMobileOpen(false);
  };

  // Encontrar el índice actual para bottom navigation
  const getCurrentBottomNavValue = () => {
    const bottomIndex = bottomNavItems.findIndex(
      (item) => location.pathname === item.path,
    );
    if (bottomIndex !== -1) return bottomIndex;

    const moreIndex = moreNavItems.findIndex(
      (item) => location.pathname === item.path,
    );
    if (moreIndex !== -1) return 4; // "Más" option

    return 0;
  };

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: designTokens.backgrounds.paper,
      }}
    >
      {/* Header del Drawer */}
      <Box
        sx={{
          background: designTokens.gradients.primary,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 64,
        }}
      >
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            color: "#000",
            fontWeight: 700,
            fontSize: { xs: "1rem", sm: "1.1rem" },
          }}
        >
          A la Vuelta 360°
        </Typography>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ color: "#000" }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: designTokens.borders.subtle }} />

      {/* Menu Items */}
      <List sx={{ flex: 1, pt: 2, px: 1 }}>
        {mainMenuItems.map((item, index) => {
          const [itemPathname, itemSearch] = item.path.includes("?") ? item.path.split("?").map((p, i) => i === 1 ? "?" + p : p) : [item.path, ""];
          const isSelected = itemSearch
            ? location.pathname === itemPathname && location.search === itemSearch
            : location.pathname === item.path;
          return (
            <Fade
              in
              key={item.text}
              timeout={300}
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    borderRadius: designTokens.radius.sm,
                    transition: designTokens.transitions.normal,
                    py: 1.5,
                    "&.Mui-selected": {
                      background: designTokens.gradients.primary,
                      color: "#000",
                      boxShadow: designTokens.shadows.glow,
                      "& .MuiListItemIcon-root": {
                        color: "#000",
                      },
                      "&:hover": {
                        background: designTokens.gradients.primaryHover,
                      },
                    },
                    "&:hover": {
                      background: "rgba(0, 201, 255, 0.08)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: isSelected ? "#000" : designTokens.colors.primary,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: isSelected ? 600 : 400,
                      fontSize: "0.9375rem",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </Fade>
          );
        })}
      </List>

      <Divider sx={{ borderColor: designTokens.borders.subtle }} />

      {/* User Menu Items */}
      <List sx={{ pt: 1, pb: 2, px: 1 }}>
        {userMenuItems.map((item) => {
          const itemPathname = item.path.split('?')[0];
          const itemSearch   = item.path.includes('?') ? '?' + item.path.split('?')[1] : '';
          const isSelected   = location.pathname === itemPathname &&
            (itemSearch === '' ? !location.search.includes('tab=') : location.search === itemSearch);
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isSelected}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  borderRadius: designTokens.radius.sm,
                  transition: designTokens.transitions.normal,
                  py: 1.5,
                  "&.Mui-selected": {
                    background: designTokens.gradients.primary,
                    color: "#000",
                    "& .MuiListItemIcon-root": {
                      color: "#000",
                    },
                  },
                  "&:hover": {
                    background: "rgba(0, 201, 255, 0.08)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isSelected ? "#000" : designTokens.colors.primary,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: "0.9375rem",
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* User Info Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${designTokens.borders.subtle}`,
          background: designTokens.backgrounds.elevated,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={user?.colaborador?.foto_url || undefined}
            sx={{
              width: 36,
              height: 36,
              background: designTokens.gradients.primary,
              color: "#000",
              fontWeight: 600,
            }}
          >
            {user?.username?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user?.username}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Administrador
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: `${designTokens.backgrounds.paper}ee`,
          backdropFilter: "blur(10px)",
          borderBottom: `1px solid ${designTokens.borders.light}`,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          {/* Logo circular - solo mobile */}
          <IconButton
            edge="start"
            onClick={() => navigate('/admin/dashboard')}
            sx={{
              mr: 1,
              display: { sm: "none" },
              p: 0,
              width: 38,
              height: 38,
              borderRadius: '50%',
              overflow: 'hidden',
              border: '2px solid rgba(255,255,255,0.15)',
              '&:hover': { border: '2px solid rgba(255,255,255,0.4)' },
            }}
          >
            <Box
              component="img"
              src={R1}
              alt="Inicio"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </IconButton>

          {/* Título responsive */}
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontSize: { xs: "1rem", sm: "1.25rem" },
              fontWeight: 600,
              textAlign: isMobile ? "center" : "left",
              background: designTokens.gradients.primary,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {isMobile
              ? (user?.colaborador?.nombre_completo || user?.nombre_completo || user?.username || "Admin")
              : "Panel de Administración"}
          </Typography>

          {/* Campana de Notificaciones */}
          <Tooltip title={alertas.length > 0 ? `${alertas.length} solicitud${alertas.length > 1 ? "es" : ""} de anticipo pendiente${alertas.length > 1 ? "s" : ""}` : "Sin notificaciones"}>
            <IconButton
              onClick={(e) => { setNotifAnchorEl(e.currentTarget); fetchAlertas(); }}
              sx={{
                mr: 0.5,
                color: alertas.length > 0 ? designTokens.colors.primary : "text.secondary",
                transition: designTokens.transitions.normal,
                animation: alertas.length > 0 ? "bellShake 2.5s ease infinite" : "none",
                "@keyframes bellShake": {
                  "0%, 85%, 100%": { transform: "rotate(0deg)" },
                  "90%": { transform: "rotate(-15deg)" },
                  "95%": { transform: "rotate(15deg)" },
                },
              }}
            >
              <Badge
                badgeContent={alertas.length}
                color="error"
                max={9}
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.65rem",
                    height: 18,
                    minWidth: 18,
                    fontWeight: 700,
                  },
                }}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Panel de Notificaciones */}
          <Popover
            open={Boolean(notifAnchorEl)}
            anchorEl={notifAnchorEl}
            onClose={() => setNotifAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                mt: 1,
                width: { xs: 340, sm: 400 },
                maxHeight: 520,
                background: designTokens.backgrounds.elevated,
                border: `1px solid ${designTokens.borders.light}`,
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            {/* Header del panel */}
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: `1px solid ${designTokens.borders.subtle}`,
                background: "rgba(0,201,255,0.06)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <NotificationsIcon sx={{ color: designTokens.colors.primary, fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={700}>
                  Solicitudes de Anticipo
                </Typography>
                {alertas.length > 0 && (
                  <Chip
                    label={alertas.length}
                    size="small"
                    color="error"
                    sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700 }}
                  />
                )}
              </Box>
              <IconButton size="small" onClick={() => setNotifAnchorEl(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Lista de solicitudes */}
            <Box sx={{ overflowY: "auto", flex: 1 }}>
              {loadingAlertas ? (
                <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {[1, 2].map((i) => (
                    <Skeleton key={i} variant="rounded" height={90} sx={{ bgcolor: "rgba(255,255,255,0.05)" }} />
                  ))}
                </Box>
              ) : alertas.length === 0 ? (
                <Box sx={{ py: 5, textAlign: "center" }}>
                  <CheckCircleIcon sx={{ fontSize: 48, color: "success.main", opacity: 0.6, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Sin solicitudes pendientes
                  </Typography>
                </Box>
              ) : (
                alertas.map((alerta, idx) => {
                  const esAnticipo = alerta.tipo_alerta === "anticipo_solicitado";
                  const esVencida  = alerta.tipo_alerta === "comision_vencida";
                  const borderColor = esAnticipo ? "#ff9800" : esVencida ? "#f44336" : designTokens.colors.primary;
                  const avatarBg    = esAnticipo
                    ? "linear-gradient(135deg,#ff9800,#f44336)"
                    : esVencida
                    ? "linear-gradient(135deg,#f44336,#b71c1c)"
                    : designTokens.gradients.primary;
                  const chipLabel   = esAnticipo ? "Anticipo" : esVencida ? "Vencida" : "Pendiente";
                  const chipColor   = esAnticipo ? "warning"  : esVencida ? "error"   : "info";
                  const montoLabel  = esAnticipo ? "anticipo" : "comisión";
                  const montoValor  = esAnticipo
                    ? parseFloat(alerta.anticipo_permitido || alerta.monto * 0.25)
                    : parseFloat(alerta.monto);
                  const montoColor  = esAnticipo ? "warning.light" : esVencida ? "error.light" : "info.light";

                  return (
                  <Box
                    key={alerta.id}
                    sx={{
                      px: 2.5,
                      py: 1.75,
                      borderBottom: idx < alertas.length - 1 ? `1px solid ${designTokens.borders.subtle}` : "none",
                      borderLeft: `3px solid ${borderColor}`,
                      "&:hover": { background: "rgba(0,201,255,0.04)" },
                    }}
                  >
                    {/* Etiqueta tipo + info colaborador */}
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                      <Avatar
                        sx={{
                          width: 34,
                          height: 34,
                          background: avatarBg,
                          color: "#000",
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          flexShrink: 0,
                        }}
                      >
                        {(alerta.colaborador_nombre || "?").charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {alerta.colaborador_nombre}
                          </Typography>
                          <Chip
                            label={chipLabel}
                            size="small"
                            color={chipColor}
                            sx={{ height: 16, fontSize: "0.6rem", fontWeight: 700, flexShrink: 0 }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                          {alerta.evento_titulo}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                          <TimeIcon sx={{ fontSize: 11, color: esVencida ? "error.light" : "text.disabled" }} />
                          <Typography variant="caption" color={esVencida ? "error.light" : "text.disabled"}>
                            Evento: {alerta.fecha_evento
                              ? new Date(alerta.fecha_evento + "T12:00:00").toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })
                              : "—"}
                            {esVencida && " · Evento concluido"}
                          </Typography>
                        </Box>
                      </Box>
                      {/* Monto */}
                      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                        <Typography variant="body2" fontWeight={800} color={montoColor}>
                          ${montoValor.toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {montoLabel}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Botones */}
                    {esAnticipo ? (
                      <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={processingId === alerta.id ? <CircularProgress size={12} color="inherit" /> : <CheckCircleIcon />}
                          disabled={processingId === alerta.id || processingId === `reject-${alerta.id}`}
                          onClick={() => handleAprobarAnticipo(alerta)}
                          sx={{ flex: 1, fontSize: "0.72rem", py: 0.5, textTransform: "none", fontWeight: 700 }}
                        >
                          Aprobar
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={processingId === `reject-${alerta.id}` ? <CircularProgress size={12} color="inherit" /> : <CancelIcon />}
                          disabled={processingId === alerta.id || processingId === `reject-${alerta.id}`}
                          onClick={() => handleRechazarAnticipo(alerta)}
                          sx={{ flex: 1, fontSize: "0.72rem", py: 0.5, textTransform: "none", fontWeight: 700 }}
                        >
                          Rechazar
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        variant={esVencida ? "outlined" : "text"}
                        color={esVencida ? "error" : "primary"}
                        onClick={() => { setNotifAnchorEl(null); navigate("/admin/comisiones"); }}
                        sx={{ fontSize: "0.7rem", textTransform: "none", pl: esVencida ? undefined : 0, fontWeight: esVencida ? 700 : 400 }}
                      >
                        {esVencida ? "Pagar comisión ahora →" : "Ver en comisiones →"}
                      </Button>
                    )}
                  </Box>
                  );
                })
              )}
            </Box>

            {/* Footer: ver todas */}
            {alertas.length > 0 && (
              <Box
                sx={{
                  px: 2.5,
                  py: 1.25,
                  borderTop: `1px solid ${designTokens.borders.subtle}`,
                  background: "rgba(0,201,255,0.04)",
                  textAlign: "center",
                }}
              >
                <Button
                  size="small"
                  onClick={() => { setNotifAnchorEl(null); navigate("/admin/comisiones"); }}
                  sx={{ color: designTokens.colors.primary, textTransform: "none", fontWeight: 600, fontSize: "0.8rem" }}
                >
                  Ver módulo de Comisiones →
                </Button>
              </Box>
            )}
          </Popover>

          {/* User Avatar */}
          <Tooltip title="Cuenta">
            <IconButton
              onClick={handleMenuOpen}
              sx={{
                p: 0.5,
                border: `2px solid transparent`,
                transition: designTokens.transitions.normal,
                "&:hover": {
                  border: `2px solid ${designTokens.colors.primary}`,
                },
              }}
            >
              <Avatar
                src={user?.colaborador?.foto_url || undefined}
                sx={{
                  width: { xs: 32, sm: 36 },
                  height: { xs: 32, sm: 36 },
                  background: designTokens.gradients.primary,
                  color: "#000",
                  fontWeight: 600,
                }}
              >
                {user?.username?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>

          {/* User Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                background: designTokens.backgrounds.elevated,
                border: `1px solid ${designTokens.borders.light}`,
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {user?.username}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Administrador
              </Typography>
            </Box>
            <Divider sx={{ borderColor: designTokens.borders.subtle }} />
            {userMenuItems.map((item) => (
              <MenuItem
                key={item.text}
                onClick={() => {
                  handleMenuClose();
                  navigate(item.path);
                }}
                sx={{ py: 1.5 }}
              >
                <ListItemIcon sx={{ color: designTokens.colors.primary }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText>{item.text}</ListItemText>
              </MenuItem>
            ))}
            <Divider sx={{ borderColor: designTokens.borders.subtle }} />
            <MenuItem
              onClick={handleLogout}
              sx={{
                py: 1.5,
                color: designTokens.colors.error,
                "&:hover": {
                  background: "rgba(255, 82, 82, 0.08)",
                },
              }}
            >
              <ListItemIcon sx={{ color: designTokens.colors.error }}>
                <Logout fontSize="small" />
              </ListItemIcon>
              <ListItemText>Cerrar Sesión</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar Navigation */}
      <Box
        component="nav"
        sx={{
          width: { sm: drawerWidth },
          flexShrink: { sm: 0 },
        }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              background: designTokens.backgrounds.paper,
              borderRight: `1px solid ${designTokens.borders.light}`,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              background: designTokens.backgrounds.paper,
              borderRight: `1px solid ${designTokens.borders.light}`,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          mt: { xs: 7, sm: 8 },
          mb: { xs: 8, sm: 0 }, // Espacio para bottom nav en mobile
          p: { xs: 2, sm: 3, md: 4 },
          transition: designTokens.transitions.normal,
        }}
      >
        <Outlet />
        <WelcomePopup
          userRol={user?.rol}
          userName={user?.colaborador?.nombre_completo || user?.username}
        />
      </Box>

      {/* Bottom Navigation - Solo Mobile */}
      {isMobile && (
        <Paper
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            background: designTokens.backgrounds.elevated,
            borderTop: `1px solid ${designTokens.borders.light}`,
            pb: "env(safe-area-inset-bottom, 0px)",
          }}
          elevation={8}
        >
          <BottomNavigation
            value={getCurrentBottomNavValue()}
            onChange={(event, newValue) => {
              if (newValue < 4) {
                navigate(bottomNavItems[newValue].path);
              }
            }}
            showLabels
            sx={{
              background: "transparent",
              height: 64,
              "& .MuiBottomNavigationAction-root": {
                color: "text.secondary",
                minWidth: 60,
                py: 1,
                "&.Mui-selected": {
                  color: designTokens.colors.primary,
                },
              },
              "& .MuiBottomNavigationAction-label": {
                fontSize: "0.625rem",
                mt: 0.5,
                "&.Mui-selected": {
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                },
              },
            }}
          >
            {bottomNavItems.map((item, index) => (
              <BottomNavigationAction
                key={item.text}
                label={item.text}
                icon={item.icon}
              />
            ))}
            <BottomNavigationAction
              label="Más"
              icon={<MoreHoriz />}
              onClick={handleMoreMenuOpen}
            />
          </BottomNavigation>

          {/* Menu "Más" para opciones adicionales */}
          <Menu
            anchorEl={moreMenuAnchor}
            open={Boolean(moreMenuAnchor)}
            onClose={handleMoreMenuClose}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            transformOrigin={{ vertical: "bottom", horizontal: "center" }}
            PaperProps={{
              sx: {
                mb: 1,
                minWidth: 200,
                background: designTokens.backgrounds.elevated,
                border: `1px solid ${designTokens.borders.light}`,
              },
            }}
          >
            {moreNavItems.map((item) => (
              <MenuItem
                key={item.text}
                onClick={() => handleNavigate(item.path)}
                selected={location.pathname === item.path}
                sx={{
                  py: 1.5,
                  "&.Mui-selected": {
                    background: "rgba(0, 201, 255, 0.12)",
                  },
                }}
              >
                <ListItemIcon sx={{ color: designTokens.colors.primary }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText>{item.text}</ListItemText>
              </MenuItem>
            ))}
            <Divider sx={{ borderColor: designTokens.borders.subtle }} />
            {userMenuItems.map((item) => (
              <MenuItem
                key={item.text}
                onClick={() => handleNavigate(item.path)}
                sx={{ py: 1.5 }}
              >
                <ListItemIcon sx={{ color: designTokens.colors.secondary }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText>{item.text}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </Paper>
      )}
    </Box>
  );
};

export default AdminLayout;
